import type { Envelope, FaqResult, FootprintResult, HistoryResult, ProbeResult, ScanResult, VisibilityProbe } from './types';

/** API base: respects Vite's base path so calls work under /ai-visibility-scan in prod. */
export const apiBase = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/api`;

export interface ProbeEngine { engine: 'openai' | 'gemini'; provider: string; name: string; label: string }

/** Friendly engine name for the client — never the specific model version (#3). */
export function engineName(s?: string | null): string {
  if (!s) return 'AI';
  if (/gemini/i.test(s)) return 'Gemini';
  if (/gpt|openai/i.test(s)) return 'GPT';
  if (/claude|anthropic/i.test(s)) return 'Claude';
  return s;
}

/** Probe engines the visitor can pick (option C). */
export async function getProbeEngines(): Promise<ProbeEngine[]> {
  const res = await fetch(`${apiBase}/probe-engines`);
  const json = (await res.json()) as Envelope<{ engines: ProbeEngine[] }>;
  return json.ok ? json.data.engines : [];
}

export async function scanUrl(url: string, force = false, progressId?: string): Promise<ScanResult> {
  const res = await fetch(`${apiBase}/scan`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, force, ...(progressId ? { progressId } : {}) }),
  });
  const json = (await res.json()) as Envelope<ScanResult>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

/** One step a running readiness scan has reached (see backend scan/progress.ts). */
export interface ScanStep { t: number; text: string }

export async function getScanProgress(id: string): Promise<{ steps: ScanStep[]; done: boolean }> {
  const res = await fetch(`${apiBase}/scan/progress/${encodeURIComponent(id)}`, { credentials: 'include' });
  const json = (await res.json()) as Envelope<{ steps: ScanStep[]; done: boolean }>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

function hostOf(url: string): string {
  try {
    return new URL(/^https?:\/\//.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** Citation footprint (FB-6): where does AI already know/cite you?
 *  Optional brandName overrides the auto-derived brand (fixes false negatives). */
export async function footprintUrl(url: string, brandName?: string, n?: number, provider?: string, queries?: string[], recallOnly = false): Promise<FootprintResult> {
  const body: Record<string, unknown> = { url }; // backend picks the sample count for footprint
  if (n) body.n = n;
  if (provider) body.provider = provider;
  if (queries?.length) body.queries = queries; // semi-automatic: the approved set (stored for later runs)
  // In semi-automatic mode Visibility already probed that set — asking again here
  // would pay twice for the same searches, so footprint does cold recall only.
  if (recallOnly) body.recallOnly = true;
  if (brandName?.trim()) body.brand = { name: brandName.trim(), domain: hostOf(url) };
  const res = await fetch(`${apiBase}/footprint`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as Envelope<FootprintResult>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

/** Draft FAQ (Q + grounded answers) for the Fix It plan. Reuses questions if given. */
export async function draftFaq(url: string, questions?: string[]): Promise<FaqResult> {
  const res = await fetch(`${apiBase}/faq`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(questions?.length ? { url, questions } : { url }),
  });
  const json = (await res.json()) as Envelope<FaqResult>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

// ── Claim the passage AI read but answered without you ───────────────────────

export type PassageVerdict = 'rewrite' | 'named' | 'no_answer';
export interface PassageFix { verdict: PassageVerdict; passage: string; suggestion: string; why: string }

/**
 * For one question the AI answered FROM your page WITHOUT naming you, find the
 * sentence on that page to change. On demand — one LLM call, deliberately not
 * part of a scan, which has a hard cost ceiling to respect.
 */
export async function suggestPassage(body: {
  pageUrl: string; question: string; brandName: string;
  aliases?: string[]; rivals?: string[]; lang?: string | null;
}): Promise<PassageFix> {
  const res = await fetch(`${apiBase}/passage`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as Envelope<{ fix: PassageFix }>;
  if (!json.ok) throw new Error(json.error);
  return json.data.fix;
}

export async function getHistory(url: string): Promise<HistoryResult> {
  const res = await fetch(`${apiBase}/history?url=${encodeURIComponent(url)}`, { credentials: 'include' });
  const json = (await res.json()) as Envelope<HistoryResult>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

/**
 * Visibility probe (smer B). Empty query → the server auto-generates them.
 *
 * Neither the question count nor the sample count is sent: both are the server's
 * to decide (admin-tunable, plan-capped, and the sample budget is what keeps a
 * run under its cost ceiling — D-31). Sending them from here silently overrode a
 * correctly-configured server, which is exactly how a raised FOOTPRINT_QUESTIONS
 * kept producing the old count.
 */
export async function probeUrl(url: string, query?: string, n?: number, provider?: string, queries?: string[]): Promise<ProbeResult> {
  const body: Record<string, unknown> = { url, provider };
  if (query?.trim()) body.queries = [query.trim()];
  else if (queries?.length) body.queries = queries;
  else {
    body.autoGenerate = true;
    if (n) body.n = n;
  }
  const res = await fetch(`${apiBase}/probe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as Envelope<ProbeResult>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}


// ── Semi-automatic mode (D-28) ───────────────────────────────────────────────

export interface PreparedQuestions {
  brand?: { name: string; domain: string; aliases?: string[] };
  analyzedPages?: { url: string; title: string }[];
  questions: string[];
  /** 'generated' = we just wrote them · 'approved' = the set you signed off before. */
  source: 'generated' | 'approved';
  /** The plan's effective cap on how many questions may be approved. */
  max?: number;
  updatedAt?: string;
  namedEntities?: string[];
  lang?: string | null;
  skipped?: 'ai-blocked' | 'thin-content' | 'http-error';
}

/**
 * Propose the questions WITHOUT running any web search, so they can be reviewed
 * and edited before any money is spent probing them. Returns the domain's stored
 * approved set when there is one; `regenerate` discards it and writes fresh ones.
 */
export async function prepareQuestions(
  url: string, n?: number, regenerate = false,
  /** What the client knows and the page cannot say (#6). */
  hints?: { rivals?: string[]; terms?: string[]; wantedFor?: string; notUs?: string },
): Promise<PreparedQuestions> {
  const res = await fetch(`${apiBase}/footprint/questions`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, ...(n ? { n } : {}), ...(regenerate ? { regenerate: true } : {}), ...(hints && Object.keys(hints).length ? { hints } : {}) }),
  });
  const json = (await res.json()) as Envelope<PreparedQuestions>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

// ── Per-page readiness (#4) ──────────────────────────────────────────────────

export interface PageScore {
  url: string; title: string; score: number; h1Count: number;
  jsonLdTypes: string[]; hasFaq: boolean; hasTables: boolean; hasLists: boolean;
  wordCount: number; numberDensityPer1k: number;
  modifiedDate: string | null; metaDescription: string | null; isEntry: boolean;
}
export interface PagesResult {
  brand: { name: string; domain: string };
  pages: PageScore[];
  /** Always false — the LLM citability test is deliberately not run per page. */
  llmRan: false;
}

/** Score every page that feeds the brand analysis, not just the URL typed in. */
export async function scanAllPages(url: string): Promise<PagesResult> {
  const res = await fetch(`${apiBase}/pages`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  const json = (await res.json()) as Envelope<PagesResult>;
  if (!json.ok) throw new Error(json.error);
  return json.data;
}

// ── Background probe jobs (D-35) ─────────────────────────────────────────────

export interface ProbeJob {
  id: string; url: string; domain: string; engine: string;
  status: 'running' | 'done' | 'failed';
  done: number; total: number;
  result: {
    brand: { name: string; domain: string; aliases?: string[] };
    analyzedPages: { url: string; title: string }[];
    engine: string; engineLabel: string;
    probes: VisibilityProbe[];
    footprint: FootprintResult | null;
    samples: number;
  } | null;
  error: string | null;
  createdAt: string; finishedAt: string | null;
}

/** Start the live test. Returns immediately — the work runs on the server. */
export async function startProbeJob(body: {
  url: string; queries: string[]; provider?: string; querySetId?: number; querySetName?: string;
}): Promise<ProbeJob> {
  const res = await fetch(`${apiBase}/probe/start`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as Envelope<{ job: ProbeJob }>;
  if (!json.ok) throw new Error(json.error);
  return json.data.job;
}

export async function getProbeJob(id: string): Promise<ProbeJob> {
  const res = await fetch(`${apiBase}/probe/jobs/${id}`, { credentials: 'include' });
  const json = (await res.json()) as Envelope<{ job: ProbeJob }>;
  if (!json.ok) throw new Error(json.error);
  return json.data.job;
}

/**
 * Poll a job to completion.
 *
 * A poll that fails is not a job that failed — a flaky moment on the client
 * should not throw away a run the server is still doing, so transient errors are
 * tolerated and only a long silence gives up.
 */
export async function waitForProbeJob(
  id: string,
  onProgress: (job: ProbeJob) => void,
  intervalMs = 2500,
): Promise<ProbeJob> {
  let consecutiveErrors = 0;
  for (;;) {
    await new Promise((r) => setTimeout(r, intervalMs));
    try {
      const job = await getProbeJob(id);
      consecutiveErrors = 0;
      onProgress(job);
      if (job.status !== 'running') return job;
    } catch (err) {
      if (++consecutiveErrors >= 8) throw err instanceof Error ? err : new Error('lost contact with the server');
    }
  }
}
